import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { formatDate } from '../../../../utils/format';

const TOTAL_RATING_STARS = 5;

const QUESTION_LABELS = {
  rating: '¿Cómo calificarías a tu mentor?',
  comment: 'Comentario',
  specialty_fit: '¿La especialidad del mentor se ajustó a tus necesidades?',
  specialtyFit: '¿La especialidad del mentor se ajustó a tus necesidades?',
};

const hasValue = (value) => value !== undefined && value !== null;

const getFirstValue = (source, keys) => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  return keys.reduce((result, key) => {
    if (hasValue(result)) {
      return result;
    }

    return hasValue(source[key]) ? source[key] : result;
  }, undefined);
};

const getQuestionLabel = (key, index) => {
  if (QUESTION_LABELS[key]) {
    return QUESTION_LABELS[key];
  }

  if (key) {
    return String(key)
      .replace(/_/g, ' ')
      .replace(/^\w/, (character) => character.toUpperCase());
  }

  return `Pregunta ${index + 1}`;
};

const normalizeAnswers = (answers) => {
  if (Array.isArray(answers)) {
    return answers.map((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return {
          key: null,
          question: getQuestionLabel(null, index),
          answer: item,
          rawAnswer: item,
        };
      }

      const key = getFirstValue(item, ['key', 'name', 'id']);
      return {
        key,
        question:
          getFirstValue(item, ['question', 'title', 'label']) ||
          getQuestionLabel(key, index),
        answer: getFirstValue(item, [
          'displayAnswer',
          'answer',
          'value',
          'response',
        ]),
        rawAnswer: getFirstValue(item, [
          'answer',
          'value',
          'response',
          'displayAnswer',
        ]),
      };
    });
  }

  if (answers && typeof answers === 'object') {
    return Object.entries(answers).map(([key, answer], index) => ({
      key,
      question: getQuestionLabel(key, index),
      answer,
      rawAnswer: answer,
    }));
  }

  return [];
};

const formatAnswer = (answer) => {
  if (answer === true) {
    return 'Sí';
  }

  if (answer === false) {
    return 'No';
  }

  if (
    answer === undefined ||
    answer === null ||
    (typeof answer === 'string' && !answer.trim())
  ) {
    return 'Sin respuesta';
  }

  if (Array.isArray(answer)) {
    return answer.map(formatAnswer).join(', ');
  }

  if (typeof answer === 'object') {
    return JSON.stringify(answer);
  }

  return String(answer);
};

const normalizeRating = (value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value.trim())
  ) {
    return null;
  }

  const rating = Number(value);

  if (
    !Number.isFinite(rating) ||
    rating < 1 ||
    rating > TOTAL_RATING_STARS
  ) {
    return null;
  }

  return rating;
};

const formatRating = (rating) =>
  Number.isInteger(rating) ? String(rating) : rating.toFixed(1);

const MentorRating = ({ value }) => {
  const rating = normalizeRating(value);

  if (rating === null) {
    return null;
  }

  const filledStars = Math.round(rating);
  const ratingLabel = formatRating(rating);

  return (
    <div
      className="survey-results__rating"
      role="img"
      aria-label={`${ratingLabel} de ${TOTAL_RATING_STARS} estrellas`}
    >
      <span className="survey-results__rating-stars" aria-hidden="true">
        {Array.from({ length: TOTAL_RATING_STARS }).map((unusedItem, index) =>
          index < filledStars ? (
            <StarIcon
              className="survey-results__rating-star is-filled"
              key={index}
            />
          ) : (
            <StarBorderIcon
              className="survey-results__rating-star"
              key={index}
            />
          )
        )}
      </span>
      <strong className="survey-results__rating-score">
        {ratingLabel}/{TOTAL_RATING_STARS}
      </strong>
    </div>
  );
};

MentorRating.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const getRoleType = (role) => {
  if (!role) {
    return '';
  }

  return typeof role === 'object' ? role.type || role.name || '' : role;
};

const isMentorRating = (answer, respondentRole) => {
  if (answer.key !== 'rating') {
    return false;
  }

  const roleType = String(getRoleType(respondentRole)).toLowerCase();
  const questionMentionsMentor = String(answer.question || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .includes('mentor');

  return ['mentee', 'none'].includes(roleType) || questionMentionsMentor;
};

const formatRole = (role) => {
  const roleType = String(getRoleType(role)).toLowerCase();

  if (roleType === 'mentor') {
    return 'Mentor';
  }

  if (roleType === 'mentee' || roleType === 'none') {
    return 'Mentee';
  }

  return roleType;
};

const formatSource = (source) => {
  const labels = {
    legacy: 'Encuesta histórica',
    platform: 'Plataforma',
    whatsapp: 'WhatsApp',
  };

  return labels[source] || '';
};

const normalizeSurveyResponse = (response, index) => {
  const nestedRespondent = response?.respondent || {};
  const respondent = {
    id: hasValue(response?.userId) ? response.userId : nestedRespondent.id,
    name: response?.userName || nestedRespondent.name,
    email: response?.userEmail || nestedRespondent.email,
    role: response?.role || nestedRespondent.role,
  };

  return {
    id: `${response?.source || 'survey'}-${
      response?.id || `response-${index}`
    }`,
    respondent,
    source: response?.source || null,
    respondedAt: response?.respondedAt || null,
    answers: normalizeAnswers(response?.answers),
  };
};

const SurveyResults = ({ surveyResponses = [] }) => {
  const responses = useMemo(
    () => surveyResponses.map(normalizeSurveyResponse),
    [surveyResponses]
  );

  return (
    <Grid container className="survey-results">
      <Grid item xs={12}>
        <div className="survey-results__heading">
          <h3>Resultados de encuesta</h3>
          <p>Respuestas registradas después de esta mentoría.</p>
        </div>

        {responses.length ? (
          <div className="survey-results__responses">
            {responses.map((response, responseIndex) => {
              const respondentName =
                response.respondent?.name ||
                response.respondent?.email ||
                'Usuario sin identificar';
              const respondentRole = formatRole(response.respondent?.role);
              const responseSource = formatSource(response.source);

              return (
                <section
                  className="survey-results__response"
                  key={response.id || responseIndex}
                >
                  <div className="survey-results__respondent">
                    <div>
                      <strong>{respondentName}</strong>
                      {respondentRole && <span>{respondentRole}</span>}
                      {responseSource && <span>{responseSource}</span>}
                    </div>
                    {response.respondedAt && (
                      <p>
                        Respondida el{' '}
                        {formatDate(response.respondedAt, 'DD/MM/YYYY HH:mm')}
                      </p>
                    )}
                  </div>

                  {response.answers.length ? (
                    <div className="survey-results__answers">
                      {response.answers.map((answer, answerIndex) => {
                        const mentorRating = isMentorRating(
                          answer,
                          response.respondent?.role
                        )
                          ? normalizeRating(answer.rawAnswer)
                          : null;

                        return (
                          <div
                            className="survey-results__answer"
                            key={`${response.id}-answer-${answerIndex}`}
                          >
                            <p className="survey-results__question">
                              {answer.question}
                            </p>
                            {mentorRating !== null ? (
                              <MentorRating value={mentorRating} />
                            ) : (
                              <p className="survey-results__answer-value">
                                {formatAnswer(answer.answer)}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="survey-results__no-detail">
                      La encuesta fue respondida, pero no contiene respuestas
                      para mostrar.
                    </p>
                  )}
                </section>
              );
            })}
          </div>
        ) : (
          <div className="survey-results__empty">
            <p>Aún no hay respuestas para esta mentoría.</p>
          </div>
        )}
      </Grid>
    </Grid>
  );
};

SurveyResults.propTypes = {
  surveyResponses: PropTypes.array,
};

export default SurveyResults;
